/* istanbul ignore file */

import React from 'react';
import PropTypes from 'prop-types';
import { get, isNumber, uniq, chain, sortBy, compact } from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import decimal from '@bingads-webui-component-legacy/humanize/decimal';
import { getDealDetailsStyle } from './deal-detils-style';
import { getTargetConstraints } from './deal-constraints';
import { MTCurrencyConvertor } from './account-currency-daily-exchange-rate';

export function getVideoConstraintDuration(dealDetails, i18n) {
  const minimumDuration = get(dealDetails, 'DealAdQualityConstraints[0].MinimumDuration');
  if (minimumDuration && isNumber(minimumDuration)) {
    return `${Math.round(minimumDuration/1000, 1)} ${i18n.getString(_TL_('seconds'))}`;
  }
  return i18n.getString(_TL_('Unavailable'));
}

export function isNetflixDeal(deal) {
  return deal.Seller && deal.Seller.toLowerCase().indexOf('netflix') > -1;
}

export function isDealDeleted(deal) {
  return deal.Error && deal.Error === 'InvalidId';
}

const AgeRanges = {
  EighteenToTwentyFive: _TL_('18-24'),
  TwentyFiveToThirtyFive: _TL_('25-34'),
  ThirtyFiveToFifty: _TL_('35-49'),
  FiftyToSixtyFive: _TL_('50-64'),
  SixtyFiveAndAbove: _TL_('65+'),
  Unknown: _TL_('Unknown'),
};

const sortAgeRanges = ageRanges => sortBy(ageRanges, ageRange => AgeRanges[ageRange]);

const formatAgeRange = (ageRange, i18n) => (AgeRanges[ageRange] ? i18n.getString(AgeRanges[ageRange]) : ageRange);

const formatAgeRanges = (ageRanges, i18n) => {
  return chain(sortAgeRanges(ageRanges))
    .map(ageRange => formatAgeRange(ageRange, i18n))
    .join(i18n.getString(_TL_(', ')))
    .value();
};

export function targetConstraintsToText(dealDetails, targetType, i18n, locationMap = {}) {
  const { mandatory, isRestricted } = getTargetConstraints(dealDetails, targetType);

  if (mandatory.length === 0) {
    if (isRestricted) {
      if (isNetflixDeal(dealDetails)) {
        return i18n.getString(_TL_('Unavailable'));
      }
      return i18n.getString(_TL_('Restricted'));
    }
    return i18n.getString(_TL_('No Restrictions'));
  }

  if (targetType === 'Location') {
    return uniq(mandatory.map((location) => {
      const locationName = locationMap[location];
      return locationName;
    })).join(', ');
  } else if (targetType === 'Age') {
    return formatAgeRanges(mandatory, i18n);
  }
  return mandatory.join(', ');
}

export const DealDetails = (props) => {
  const { i18n, dealDetails, style, shouldShowGenre } = props;
  const classes = useDefaultStyles(getDealDetailsStyle);
  const dealOptions = compact([{
    name: i18n.getString(_TL_('Publisher')),
    value: dealDetails.Seller,
  }, {
    name: i18n.getString(_TL_('Minimum bid ')),
    value: dealDetails.MinimumBid ?
      `${decimal.fromFloatToString(dealDetails.MinimumBid, 2)} ${MTCurrencyConvertor[dealDetails.Currency] || 'USD'}` : `${i18n.getString(_TL_('Unavailable'))}`,
  }, {
    name: i18n.getString(_TL_('Duration ')),
    value: `${getVideoConstraintDuration(dealDetails, i18n)}`,
  }, {
    name: i18n.getString(_TL_('Country')),
    value: dealDetails.CountryName || i18n.getString(_TL_('Everywhere')),
  }, {
    name: i18n.getString(_TL_('Device targeting')),
    value: targetConstraintsToText(dealDetails, 'Device', i18n),
  }, {
    name: i18n.getString(_TL_('Gender targeting')),
    value: targetConstraintsToText(dealDetails, 'Gender', i18n),
  }, {
    name: i18n.getString(_TL_('Age targeting')),
    value: dealDetails.AgeRange || targetConstraintsToText(dealDetails, 'Age', i18n),
  }, {
    name: i18n.getString(_TL_('Audience targeting')),
    value: targetConstraintsToText(dealDetails, 'Audience', i18n),
  }, shouldShowGenre && {
    name: i18n.getString(_TL_('Genre targeting')),
    value: dealDetails.Genre ? _TL_('Available') : _TL_('Unavailable'),
  }]);

  return (
    <div className={classes.dealDetails} style={style} >
      <div>
        <h5>{i18n.getString(_TL_('Media deal details'))}</h5>
        <div className="content-wrapper" >
          {dealOptions.map(option => (
            <div key={option.name} className="option-wrapper" >
              <div className="option-name" >{option.name}</div>
              <div className="option-value" >{option.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

DealDetails.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dealDetails: PropTypes.shape({
    Seller: PropTypes.string,
    StartDate: PropTypes.string,
    EndDate: PropTypes.string,
    Genre: PropTypes.string,
    Currency: PropTypes.string,
    Duration: PropTypes.string,
    CountryName: PropTypes.string,
    MinimumBid: PropTypes.number,
    AskPrice: PropTypes.number,
  }).isRequired,
  style: PropTypes.shape({}),
};

DealDetails.defaultProps = {
  style: {},
};
